<template>
  <main>
    <LmDocumentsV1
      v-if="lumbreVersion === 1"
      ref="lmDocumentsV1"
      :is-on-edit-mode="isOnEditMode"
      :isFatherProcessing="isProcessing"
      @unsubscribe-sockets="unsubscribeSockets"
    />
    <template v-else>
      <template v-if="accessDeniedFeedback">
        <section class="access-denied-feedback-content">
          <LmSvgIcon
            icon="LmAccessDeniedIcon"
            wrapperClass="my-4 d-flex align-items-center flex-column"
            text="No tiene permisos para acceder a este documento."
            width="12em"
          />
        </section>
      </template>
      <template v-else>
        <template v-if="!isLoading">
          <section class="page-header">
            <div class="d-flex align-items-center">
              <span class="clickable mr-2" @click="$router.go(-1)" v-if="showGoBackButton">
                <i class="mdi mdi-chevron-left" style="font-size: 2rem"></i>
              </span>
              <div>
                <h2 class="mb-0 d-flex align-items-center">
                  {{ pageTitle }}
                  <span v-if="documentForm.pdfSize" style="font-size: .9rem" class="ml-3">
                    <b-badge :variant="sizeIndicator(documentForm.pdfSize)">{{ formatBytes(documentForm.pdfSize) }}</b-badge>
                  </span>
                </h2>
                <h6 class="mt-0 d-none d-md-block">{{ pageSubTitle }}</h6>
              </div>
            </div>
            <div class="d-none d-md-flex align-items-center">
              <div v-if="hasAssociatedProcesses" class="d-flex align-items-center">
                <template v-if="associatedProcesses.length === 1">
                  <div>
                    <div class="text-uppercase" style="font-size: .65rem; text-align: initial">{{ $t('related_process') }}</div>
                    <a-popover class="pl-0" placement="left">
                      <template slot="content">
                        <span class="text-uppercase" style="font-size: .65rem">{{ associatedProcesses[0].processName }}</span>
                      </template>
                      <a-button type="link">
                        <b-link :to="`/tramites/${associatedProcesses[0].processTypeId}/editar/${associatedProcesses[0]._id}?viewer`">
                          <h4 class="mt-0">{{ associatedProcesses[0].processNumber }}</h4>
                        </b-link>
                      </a-button>
                    </a-popover>
                  </div>
                </template>
                <template v-else>
                  <span class="mt-1 text-primary font-weight-bold clickable" @click="showAssociatedProcesses = true">{{ $t('related_processes_count', {processCount : associatedProcesses.length}) }}</span>
                  <a-modal v-model="showAssociatedProcesses" :title="$t('related_processes')" :footer="null">
                    <div v-for="(item, index) of associatedProcesses" :key="index">
                      <a-popover placement="right">
                        <template slot="content">
                          <span class="text-uppercase" style="font-size: .65rem">{{ item.processName }}</span>
                        </template>
                        <a-button type="link">
                          <b-link :to="`/tramites/${item.processTypeId}/editar/${item._id}?viewer`">
                            <h4 class="mt-1">{{ item.processNumber }}</h4>
                          </b-link>
                        </a-button>
                      </a-popover>
                    </div>
                  </a-modal>
                </template>
              </div>
              <component
                :is="externalParticipantLink ? 'a-dropdown-button' : 'a-button'"
                @click="onResendEmailToExternal"
                v-if="showResendEmailButton"
                :loading="isProcessing"
                :trigger="['click']"
              >
                Reenviar email a {{ documentForm.lumbreNextParticipant.firstname + ' ' + documentForm.lumbreNextParticipant.lastname }}
                <a-menu slot="overlay">
                  <a-menu-item key="1" @click="onCopyExternalLink"> <a-icon type="link" />Copiar link</a-menu-item>
                </a-menu>
              </component>
              <div
                v-for="(item, index) of stepsButtons"
                :key="index"
                :class="{'ml-3': item.condition}"
              >
                <lm-button-v2
                  v-if="item.condition && (item.position === 'top' || item.position === 'both')"
                  v-bind="item"
                  :shape="item.icon ? 'circle': null"
                  :loading="isProcessing"
                  @click="item.click"
                >
                  {{ !item.icon ? item.label : null }}
                </lm-button-v2>
              </div>
              <div v-if="!hasStepsButtonsOnTop">
                <a-button
                  v-if="documentForm.lumbreStatusName"
                  :disabled="true"
                  :loading="isProcessing"
                  :class="`status-button color-${documentForm.lumbreStatus.id}`"
                >
                  <template >
                    {{ documentForm.lumbreStatusName }}
                  </template>
                </a-button>
              </div>
            </div>
            <a-dropdown class="d-block d-md-none" :trigger="['click']">
              <a-menu slot="overlay">
                <a-menu-item
                  v-for="(item, index) of stepsButtons"
                  :key="index"
                  :class="{'d-none': !item.condition}"
                  :disabled="isProcessing"
                  style="font-size: 1.1rem; padding: 10px 14px;"
                  @click="item.click"
                >
                  {{ !item.icon ? item.label : null }}
                </a-menu-item>
              </a-menu>
              <lm-button-v2 type="link" size="large">
                <a-icon type="more" style="color: black; font-size: 24px;" />
              </lm-button-v2>
            </a-dropdown>
          </section>

          <section class="page-content">
            <div
              v-if="!documentWasStarted || documentIsOnEditionStatus"
              class="bg-light rounded-lg p-0"
            >
              <div class="d-none d-md-block">
                <a-steps
                  v-model="currentStep"
                  type="navigation"
                  size="small"
                >
                  <a-step
                    :title="$t('document_configuration')"
                    :status="stepStatus(0)"
                  />
                  <a-step
                    v-if="!isOnePersonFormat"
                    :title="$t('workflow')"
                    :status="stepStatus(1)"
                  />
                  <a-step
                    v-if="!documentWasStarted && !isOnePersonFormat"
                    :title="$t('communication')"
                    :status="stepStatus(2)"
                  />
                </a-steps>
              </div>

              <section class="rounded-lg bg-white position-relative">
                <!-- Step 0 -->
                <div v-show="currentStep === 0">
                  <a-alert
                    v-if="documentWasStarted && documentIsOnEditionStatus && collaboratorsCopy.length"
                    :message="$t('optimizing_content')"
                    class="mb-3"
                    banner
                  >
                    <div slot="description">
                      <small class="d-block mb-2 pr-2">{{$t('only_fillable_fields')}}</small>
                      <div>
                        <a-switch v-model="showAllFormFields" />
                        <small class="ml-2">{{$t('show_full_document')}}</small>
                      </div>
                    </div>
                  </a-alert>
                  <portal to="documentName">
                    <LmTextInput
                      v-if="showFormbuilder && !documentWasStarted"
                      class="col-12 px-0"
                      id="documentName"
                      v-model="$v.documentForm.documentName.$model"
                      :v="validation('documentForm.documentName')"
                      :label="$t('name')"
                      :placeholder="$t('name')"
                      :disabled="!actionsAvailables.canBeEditedByMe && isOnEditMode"
                    />
                  </portal>
                  <LmFormBuilder
                    v-if="showFormbuilder"
                    ref="lmFormbuilder"
                    style="max-width: 100vw; padding: 10px 20px;"
                    disablePageTitleData
                    :isParentOnEditMode="isOnEditMode"
                    :isDisabled="disableFormbuilder"
                    :endpointUriwhereGetComponents="formbuilderUri"
                    :contentTemplateData="documentForm"
                    :formValues="documentFormCopy"
                    :editors="collaboratorsCopy"
                    :compactMode="!showAllFormFields && documentWasStarted"
                    @formObjectToSendToServerChange="updateDocumentForm"
                    @editor-change="onEditorChange"
                    @hide-collaborators-workflow="onHideCollaborators"
                  />
                </div>

                <!-- Step 1 -->
                <div v-show="currentStep === 1" v-if="!isOnePersonFormat">
                  <LmParticipants
                    v-if="lumbreVersion == 1"
                    class="p-4"
                    :label="$t('users')"
                    :placeholder="$t('lm_participants_subtitle')"
                    v-model="documentForm.participants"
                    :canAddReviewers="actionsAvailables.canAddReviewers"
                    :canAddExternalParticipants="actionsAvailables.canAddExternalParticipants"
                  />
                  <LmDocumentWorkflow
                    v-if="lumbreVersion == 2"
                    ref="documentWorkflow"
                    :key="lmDocumentWorkflowKey"
                    v-model="documentForm.workflow"
                    :isOnEditMode="isOnEditMode"
                    :hideInPerson="!Boolean(documentTypeForm.allowInPersonParticipant)"
                    :hideCollaborators="hideWorkflowCollaborators"
                    :formCollaborators="collaboratorsCopy"
                    :canAddExternalParticipants="Boolean(documentTypeForm.typeAllowExternalParticipants)"
                    @on-change-collaborator="currentStep = 0"
                  />
                </div>

                <!-- Step 2 -->
                <template v-if="currentStep === 2 && !documentWasStarted && !isOnePersonFormat">
                  <b-tabs card class="border">
                    <b-tab :title="$t('recipients')" key="recipients" v-if="showRecipientsTab">
                      <LmRecipients
                        v-model="documentForm.recipients"
                        :canAddExternalRecipients="actionsAvailables.canAddExternalRecipients"
                      />
                    </b-tab>
                    <b-tab :title="$t('share_with')" key="sharewith" v-if="showShareWithTab">
                      <LmShareWith
                        v-model="documentForm.shareWith"
                      />
                    </b-tab>
                    <b-tab :title="$t('viewers')" key="viewers" v-if="showViewersTab">
                      <LmViewers
                        v-model="documentForm.viewers"
                      />
                    </b-tab>
                  </b-tabs>
                </template>
              </section>

              <div class="d-flex m-4" style="gap: .5rem">
                <lm-button-v2
                  v-if="showPrevButton"
                  :loading="isProcessing"
                  @click="onPrevStep"
                >
                  {{ $t('previous') }}
                </lm-button-v2>
                <lm-button-v2
                  v-if="showNextButton"
                  :loading="isProcessing"
                  @click="onNextStep"
                >
                  {{ $t('next') }}
                </lm-button-v2>
                <!-- <lm-button-v2
                  v-if="showSaveButton"
                  class="d-lg-none"
                  type="primary"
                  :loading="isProcessing"
                  @click="onSave"
                >
                  {{ $t('save') }}
                </lm-button-v2> -->
              </div>
              <div class="d-flex ml-2 mb-4" >
                <div
                  v-for="(item, index) of stepsButtons"
                  :key="index"
                  :class="{'ml-3': item.condition}"
                >
                  <lm-button-v2
                    v-if="item.condition && (item.position === 'bottom' || item.position === 'both')"
                    v-bind="item"
                    :shape="item.icon ? 'circle': null"
                    :loading="isProcessing"
                    @click="item.click"
                  >
                    {{ !item.icon ? item.label : null }}
                  </lm-button-v2>
                </div>
              </div>
            </div>

            <div v-else class="bg-light rounded-lg p-0">
              <div class="d-none d-md-block p-3">
                <a-steps v-model="currentStep">
                  <a-step status="finish" :title="$t('document')">
                    <a-icon slot="icon" type="file-pdf" />
                  </a-step>
                  <a-step status="finish" :title="$t('workflow')" v-if="!isOnePersonFormat">
                    <a-icon slot="icon" type="fork" />
                  </a-step>
                </a-steps>
              </div>

              <section class="p-4 rounded-lg bg-white position-relative">
                <!-- Step 0 -->
                <div v-show="currentStep === 0">
                  <template v-if="showPdfDocument">
                    <LmFileViewer v-if="enableFileViewer" :files="fileList" />
                    <template v-else>
                      <b-button
                        v-if="flattedFileList.length > 1"
                        class="position-absolute prev-btn"
                        variant="primary"
                        @click="pdfDocumentPosition--"
                        :disabled="pdfDocumentPosition === 0"
                      >
                        <i class="mdi mdi-chevron-left"></i>
                      </b-button>
                      <iframe
                        id="pdfViewer"
                        :src="flattedFileList[pdfDocumentPosition].path"
                        :height="700"
                        width="100%"
                      ></iframe>
                      <b-button
                        v-if="flattedFileList.length > 1"
                        class="position-absolute next-btn"
                        variant="primary"
                        @click="pdfDocumentPosition++"
                        :disabled="pdfDocumentPosition === flattedFileList.length - 1"
                      >
                        <i class="mdi mdi-chevron-right"></i>
                      </b-button>
                    </template>
                  </template>
                </div>

                <!-- Step 1 -->
                <div v-show="currentStep === 1">
                  <LmParticipants
                    v-if="lumbreVersion == 1"
                    :label="$t('users')"
                    :placeholder="$t('lm_participants_subtitle')"
                    v-model="documentForm.participants"
                    disabled
                    :canAddReviewers="actionsAvailables.canAddReviewers"
                    :canAddExternalParticipants="actionsAvailables.canAddExternalParticipants"
                  />
                  <LmDocumentWorkflow
                    v-if="lumbreVersion == 2 && !isOnePersonFormat"
                    :key="lmDocumentWorkflowKey"
                    ref="documentWorkflow"
                    v-model="documentForm.workflow"
                    :isOnEditMode="isOnEditMode"
                    :hideCollaborators="hideWorkflowCollaborators"
                    :hideInPerson="!Boolean(documentTypeForm.allowInPersonParticipant)"
                    :formCollaborators="collaboratorsCopy"
                  />
                </div>

              </section>
            </div>
            <LmToolbar
              ref="toolbar"
              class="bg-light"
              style="padding-top: 0"
              contentClass="border-0 shadow-none"
              :isOnEditMode="isOnEditMode"
              :items="toolbarItems"
              :config="toolbarConfig"
              @onContentTemplateChange="onContentTemplateChange"
              @onUpdateExpirationDate="onUpdateExpirationDate"
              @change="onToolbarModeChange"
            />
          </section>
          <lmConfirmationModal ref="lmConfirmationModalLicenses" title="Aviso" />
          <lmConfirmationModal
            ref="lmConfirmationModalFirmarToken" title="¿Está seguro?"
            message="<h4>Va a iniciar el proceso de firma del documento.<br><br></h4>"
          />
          <lmConfirmationModal
            ref="immediateActionModal"
            :ok-title="$t('accept')"
            :cancel-title="$t('i_will_do_it_later')"
          />
          <LmSendOTP
            ref="lmValidationModalFirmarToken" title="Aviso"
            :message="$t('token_code_sent')" @ok="setToken"
          />
          <LmSignaturePad ref="lmSignaturePad" />
          <WacomSignaturePad ref="WacomSignaturePad" />
        </template>
        <LmLoading v-else />
      </template>
    </template>
  </main>
</template>

<script>
import { defineComponent } from 'vue'

// Components
import LmToolbar from '@/components/lmToolBar'
import LmFormBuilder from '@/components/LmFormBuilder'
import LmFileViewer from '@/components/LmFileViewer'
import LmParticipants from '@/components/form/elements/LmParticipants'
import LmRecipients from '@/components/form/elements/LmRecipients'
import LmShareWith from '@/components/form/elements/LmShareWith'
import LmViewers from '@/components/form/elements/LmViewers'
import LmDocumentWorkflow from '@/components/LmDocumentWorkflow'
import LmButtonV2 from '@/components/form/elements/LmButtonV2'
import LmTextInput from '@/components/form/elements/LmTextInput'
import lmConfirmationModal from '@/components/modals/LmConfirmationModal'
import LmSendOTP from '@/components/modals/LmSendOTP'
import LmSignaturePad from '@/components/LmSignaturePad'
import LmDocumentsV1 from '@/views/LmDocumentsNewOrEdit-2'
import WacomSignaturePad from '@/components/WacomSignaturePad'

// Utils
import lmToasts from '@/mixins/lmToasts.js'
import lmFormatBytes from '@/mixins/lmFormatBytes.js'
import lmDocumentsMixin from '@/mixins/lmDocumentsMixin.js'
import commonsApi from '@/api/commons'
import { required } from 'vuelidate/lib/validators'
import _ from 'lodash'

// Vuex
import { mapMutations, mapGetters } from 'vuex'

import { notification } from 'ant-design-vue'

export default defineComponent({
  mixins: [lmDocumentsMixin, lmToasts, lmFormatBytes],

  components: {
    LmDocumentsV1,
    LmToolbar,
    LmFormBuilder,
    LmFileViewer,
    LmParticipants,
    LmRecipients,
    LmShareWith,
    LmViewers,
    LmDocumentWorkflow,
    LmButtonV2,
    LmTextInput,
    lmConfirmationModal,
    LmSendOTP,
    LmSignaturePad,
    WacomSignaturePad
  },

  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    }
  },

  provide () {
    const document = {}
    for (const key in this.context) {
      Object.defineProperty(document, key, {
        enumerable: true,
        get: () => this.context[key]
      })
    }
    return { document, toolbarCtx: 'documents' }
  },

  data: () => ({
    enableFileViewer: process.env?.VUE_APP_ENABLE_FILE_VIEWER === 'yes',
    lumbreSignerPort: process.env?.VUE_APP_LUMBRESIGNER_PORT || '8081',
    lumbreVersion: Number(process.env?.VUE_APP_DOCUMENTS_VERSION) || 1,
    isProcessing: false,
    isLoading: false,
    isOnLicense: false,
    lmDocumentWorkflowKey: 0,
    accessDeniedFeedback: false,
    actionsAvailables: {
      canBeStartedByMe: false,
      canBeEditedByMe: false,
      canBeSignedByMe: false,
      canBeRejectedByMe: false,
      canBeSendToSignByMe: false,
      canReSendEmailToExternalSigner: false,
      canBeReviewedByMe: false,
      canBeTakedByMe: false,
      canBeDeletedByMe: false,
      canAddReviewers: true,
      canAddExternalParticipants: true,
      canAddExternalRecipients: true
    },
    toolbarMode: null,
    currentStep: 0,
    stepsMovements: [],
    step0completed: false,
    step1completed: false,
    step2completed: true,

    showAssociatedProcesses: false,
    hasAssociatedProcesses: false,
    associatedProcesses: null,

    hideWorkflowCollaborators: false,

    otpToken: null,
    handSignature: null,
    // nota. esta prop existe por un bug
    collaboratorsCopy: [],
    documentForm: {
      documentName: '',
      viewers: { users: [], areas: [], subareas: [] },
      workflow: [],
      collaborators: [],
      lumbreStatusName: null,
      lumbreStatus: {
        id: null
      },
      participants: [],
      recipients: {
        users: [],
        areas: [],
        subareas: [],
        groups: [],
        emails: []
      },
      shareWith: [],
      expirationDate: null
    },
    documentFormCopy: {},
    documentTypeForm: {},
    attachmentDocuments: [],
    pdfDocumentPosition: 0,
    showAllFormFields: false,
    externalParticipantLink: undefined,
    signatureFilePath: null,
    signatureMetaFilePath: null,
    signatureGeoLocation: false,
    enableExpirationDate: process.env?.VUE_APP_ENABLE_DOCUMENTSTYPE_EXPIRATION_DATE === 'yes'
  }),

  validations: {
    documentForm: {
      documentName: {
        required
      }
    }
  },

  computed: {
    ...mapGetters('processes', ['processPathToGoBack']),
    context () {
      return {
        lumbreVersion: this.lumbreVersion,
        isStarted: this.documentWasStarted,
        isOnEditMode: this.isOnEditMode,
        isComunicable: this.typeComunicable
      }
    },
    disableInPersonGeoLocationValidations () {
      return process.env?.VUE_APP_ENABLE_IN_PERSON_GEOLOCATION_VALIDATIONS !== 'yes'
    },
    documentTypeId () {
      return this.$route.params?.docTypeId || null
    },
    documentId () {
      return this.$route.params?.id || null
    },
    pageTitle () {
      return !this.isOnEditMode ? 'Crear Nuevo Documento' : this.documentForm.documentName || 'Nombre del documento'
    },
    allowExpirationDate () {
      return this.documentTypeForm?.allowExpirationDate || false
    },
    pageSubTitle () {
      return this.documentTypeForm.typeAlias
    },
    isOnePersonFormat () {
      return this.documentTypeForm.documentsFormat === 'oneperson'
    },
    documentWasStarted () {
      if (this.lumbreVersion === 2) {
        return this.documentForm.started || false
      }
      if (this.lumbreVersion === 1) {
        return this.showPdfDocument
      }
    },
    documentIsOnEditionStatus () {
      return this.documentForm.lumbreStatus.id === '2'
    },
    typeComunicable () {
      return this.documentTypeForm.typeComunicable === 'comunicable'
    },
    typePublic () {
      return this.documentTypeForm.typeVisibility === 'public'
    },
    typeSignature () {
      return this.documentTypeForm.typeSignature
    },
    nextParticipantInCharacterOf () {
      return this.documentForm.lumbreNextParticipant?.inCharacterOf || null
    },
    nextParticipantName () {
      return this.documentForm.lumbreNextParticipant ? this.documentForm.lumbreNextParticipant.firstname + ' ' + this.documentForm.lumbreNextParticipant.lastname : ''
    },
    showPrevButton () {
      return this.currentStep !== 0 && !this.documentWasStarted
    },
    showNextButton () {
      const steps = document.querySelectorAll('.ant-steps-item').length
      return this.currentStep < steps && !this.documentWasStarted
    },
    showGoBackButton () {
      return true
    },
    showStartButton () {
      const invalidParticipants = this.checkForInvalidParticipantsConditions()
      return ((!this.isOnEditMode && !this.isOnePersonFormat) || (this.isOnEditMode && this.actionsAvailables.canBeStartedByMe) || (this.lumbreVersion === 1 && this.actionsAvailables.canBeEditedByMe)) &&
      this.step0completed && this.step1completed && !invalidParticipants
    },
    showSaveButton () {
      return ((this.isOnEditMode && this.actionsAvailables.canBeEditedByMe) || (!this.isOnEditMode && this.isOnePersonFormat)) && !this.documentForm.deleted
    },
    showSaveAsDraftButton () {
      return !this.isOnEditMode && !this.$v.$invalid && !this.isOnePersonFormat
    },
    showInPersonSignButton () {
      return this.actionsAvailables.canDelegateSignature
    },
    showSignButton () {
      return this.isOnEditMode && this.actionsAvailables.canBeSignedByMe
    },
    showReviewButton () {
      return this.isOnEditMode && this.actionsAvailables.canBeReviewedByMe
    },
    showTakeButton () {
      return this.isOnEditMode && this.actionsAvailables.canBeTakedByMe
    },
    showRejectButton () {
      return this.isOnEditMode && this.actionsAvailables.canBeRejectedByMe
    },
    showDeleteButton () {
      return this.isOnEditMode && this.actionsAvailables.canBeDeletedByMe && !this.documentForm.deleted
    },
    showResendEmailButton () {
      return this.isOnEditMode && this.documentWasStarted && this.actionsAvailables.canReSendEmailToExternalSigner
    },
    disableFormbuilder () {
      return this.isOnEditMode && !this.actionsAvailables.canBeEditedByMe
    },
    showFormbuilder () {
      return Boolean(this.documentTypeForm?.formbuilderId)
    },
    showRecipientsTab () {
      return (this.actionsAvailables.canBeEditedByMe || !this.isOnEditMode) && this.typeComunicable && this.lumbreVersion === 1
    },
    showShareWithTab () {
      return this.actionsAvailables.canBeEditedByMe || !this.isOnEditMode
    },
    showViewersTab () {
      return !this.typePublic && (this.actionsAvailables.canBeEditedByMe || !this.isOnEditMode)
    },
    disableComments () {
      if (this.documentTypeForm.typeComments === 'always') {
        return false
      }
      if (this.documentTypeForm.typeComments === 'in_progress') {
        if (this.documentForm.lumbreStatus.id !== '6') {
          return false
        }
        return true
      }
      return false
    },
    hideComments () {
      if (
        this.documentTypeForm.typeComments === 'in_progress' &&
        this.documentForm.lumbreStatus.id === '6' &&
        this.documentTypeForm.typeHideCommentsOnFinished
      ) {
        return true
      }
      if (this.documentTypeForm.typeComments === 'never') {
        return true
      }
      return false
    },
    toolbarItems () {
      let items = []
      if (this.isOnEditMode) {
        if (process.env?.VUE_APP_DISABLE_TAGS !== 'yes') {
          items.push('tags')
        }
        items = items.concat([
          'documentMovements',
          'auditMovements',
          'documentModel'
        ])
        if (!this.hideComments) {
          items.unshift('comments')
        }
        if (this.lumbreVersion === 2) {
          if (!this.typePublic) {
            items.push('documentViewers')
          }
          if (this.documentForm.shareWith?.length) {
            items.push('documentShareWith')
          }
        }
        if (this.actionsAvailables.canBeEditedByMe) {
          items.unshift('contentTemplates')
          if (!this.typePublic) {
            items.push('documentViewers')
          }
          items.push('documentPreview')
          if (this.enableExpirationDate && this.allowExpirationDate) {
            items.push('documentExpirationDate')
          }
        } else {
          if (this.typeComunicable && this.lumbreVersion === 1) {
            items.push('documentRecipients')
          }
        }
      } else {
        items = items.concat([
          'contentTemplates',
          'documentPreview'
        ])
      }
      return items
    },
    toolbarConfig () {
      return {
        comments: {
          linkType: 'documents',
          disabled: this.disableComments
        },
        documentPreview: {
          documentTypeId: this.documentTypeId,
          dataToRender: this.documentForm
        },
        documentExpirationDate: {
          expirationDate: this.documentForm.expirationDate
        },
        documentModel: {
          formbuilderPath: this.formbuilderUri,
          dataPathOnEditMode: '/documents/' + this.documentId,
          editors: this.collaboratorsCopy
        },
        documentRecipients: {
          recipients: this.documentForm.recipients
        },
        documentViewers: {
          viewers: this.documentForm.viewers
        },
        documentShareWith: {
          shareWith: this.documentForm.shareWith
        },
        contentTemplates: {
          path: `/contenttemplate/${this.documentTypeId}/documenttypes/all`,
          template: this.documentForm,
          reference: this.documentTypeId
        },
        auditMovements: {
          serviceName: 'documents'
        },
        tags: {
          serviceName: 'documents'
        }
      }
    },
    formbuilderUri () {
      return '/formbuilder/' + this.documentTypeForm.formbuilderId
    },
    fromProcess () {
      return Boolean(this.processPathToGoBack)
    },
    pdfDocumentPath () {
      return this.documentForm?.documentInPdf?.src
    },
    showPdfDocument () {
      return Boolean(this.pdfDocumentPath)
    },
    fileList () {
      if (!this.showPdfDocument) return null
      return [
        {
          ...this.documentForm.documentInPdf,
          path: this.pdfDocumentPath,
          attachments: this.attachmentDocuments
        }
      ]
    },
    flattedFileList () {
      if (!this.showPdfDocument) return null
      return [
        this.documentForm.documentInPdf,
        ...this.attachmentDocuments
      ]
    },
    hasStepsButtonsOnTop () {
      return this.stepsButtons.some(s => (s.position === 'top' || s.position === 'both') && s.condition).length > 0
    },
    stepsButtons () {
      return [
        {
          label: this.fromProcess ? this.$t('go_back_to_process') : this.$t('cancel'),
          type: 'secondary',
          position: 'bottom',
          condition: true,
          click: () => this.onCancel()
        },
        {
          label: this.$t('save'),
          // icon: 'save',
          type: 'primary',
          position: 'both',
          condition: this.showSaveButton,
          click: () => this.onSave()
        },
        {
          label: this.$t('save_as_draft'),
          // icon: 'save',
          type: 'info',
          position: 'both',
          condition: this.showSaveAsDraftButton,
          click: () => this.onSave()
        },
        {
          label: this.$t('start'),
          type: 'success',
          position: 'both',
          condition: this.showStartButton,
          click: () => this.onBeforeStart()
        },
        {
          label: this.$t('sign'),
          type: 'primary',
          position: 'top',
          condition: this.showSignButton,
          click: () => this.onBeforeSign()
        },
        {
          label: this.$t('inperson_sign', {
            name: this.nextParticipantName
          }),
          type: 'primary',
          position: 'top',
          condition: this.showInPersonSignButton,
          click: () => this.onBeforeSign()
        },
        {
          label: this.$t('approve'),
          type: 'primary',
          position: 'top',
          condition: this.showReviewButton,
          click: () => this.onReviewConfirmation()
        },
        {
          label: this.$t('delete'),
          type: 'danger',
          position: 'top',
          condition: this.showDeleteButton,
          click: () => this.onDelete()
        },
        // {
        //   label: this.$t('resend_email', {
        //     name: this.nextParticipantName
        //   }),
        //   type: 'link',
        //   condition: this.showResendEmailButton,
        //   click: () => this.onResendEmailToExternal()
        // },
        {
          label: this.$t('take'),
          type: 'success',
          position: 'both',
          condition: this.showTakeButton,
          click: () => this.onTake()
        },
        {
          label: this.$t('reject'),
          type: 'danger',
          confirmation: true,
          confirmationTitle: this.$t('reject'),
          confirmationMessage: this.$t('reject_confirmation'),
          position: 'top',
          condition: this.showRejectButton,
          click: () => this.onReject()
        }
      ]
    },
    inPersonSignatureValidations () {
      return this.documentTypeForm?.inPersonSignatureValidations || []
    },
    internalSignatureValidations () {
      return this.documentTypeForm?.internalSignatureValidations || []
    },
    internalSignatureValidationsCapture () {
      return this.documentTypeForm?.internalSignatureValidationsCapture || null
    }
  },

  methods: {
    ...mapMutations('processes', ['setDocument', 'clearProcessesStore']),

    validation (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },

    onPrevStep () {
      this.currentStep--
    },

    onNextStep () {
      this.currentStep++
    },

    onCopyExternalLink () {
      navigator.clipboard.writeText(this.externalParticipantLink)
      this.lmNotificationSuccessMessage({
        description: 'Copiado al portapapeles correctamente'
      })
    },
    onCancel () {
      if (this.fromProcess) {
        this.$router.push(this.processPathToGoBack)
      } else {
        this.$router.go(-1)
      }
    },
    async onSave () {
      try {
        this.isProcessing = true

        // Validaciones
        // this.validateParticipants()
        await this.validateForm(this.$v)
        if (this.$refs?.lmFormbuilder) {
          await this.$refs.lmFormbuilder.validate()
        }

        const dataToSendToServer = {
          ...this.documentForm,
          collaborators: this.collaboratorsCopy,
          documentTypeId: this.documentTypeId
        }
        let response
        if (!this.isOnEditMode) {
          // nuevo
          response = await commonsApi.axiosPostWrapper('/documents/', dataToSendToServer)
          if (this.fromProcess) {
            this.lmNotificationSuccessMessage({
              message: this.$t('good_title'),
              description: this.$t('document_created_success_msg', { documentName: response.data?.documentName })
            })
          } else {
            this.lmModalSuccessWithButtons({
              modalClosable: false,
              modalKeyboard: false,
              resultSubtitle: '',
              message: this.$t('document_created_success_msg', { documentName: response.data?.documentName }),
              buttons: [
                {
                  btnLink: '/inbox',
                  btnText: this.$t('continue')
                },
                {
                  btnLink: `/documentos/${this.documentTypeId}/editar/${response.data._id}`,
                  btnText: this.$t('view_document')
                }
              ]
            })
          }
        } else {
          // editar
          response = await commonsApi.axiosPutWrapper(`/documents/update/${this.$route?.params?.id}`, dataToSendToServer)

          this.documentForm = Object.assign(this.documentForm, response.data)

          if (this.documentIsOnEditionStatus) {
            this.lmNotificationSuccessMessage({
              message: this.$t('good_title'),
              description: this.$t('document_updated_success_shared_editor_msg', { documentName: response.data?.documentName })
            })
          } else {
            this.lmNotificationSuccessMessage({
              message: this.$t('good_title'),
              description: this.$t('document_updated_success_msg', { documentName: response.data?.documentName })
            })
          }
        }
        // Si este documento se crea como parte de un proceso (trámite)
        if (this.fromProcess) {
          this.setDocument({
            id: response.data._id,
            name: response.data.documentName
          })
          this.$router.push(this.processPathToGoBack)
        } else {
          // await this.getDocument()
          // this.refreshVueComponents()
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },

    async quickUpdate () {
      try {
        this.isProcessing = true
        await this.validateForm(this.$v)
        await this.$refs.lmFormbuilder.validate()

        const dataToSendToServer = {
          ...this.documentForm,
          collaborators: this.collaboratorsCopy,
          documentTypeId: this.documentTypeId
        }

        let response
        if (!this.isOnEditMode) {
          // nuevo
          response = await commonsApi.axiosPostWrapper('/documents/', dataToSendToServer)
        } else {
          // editar
          response = await commonsApi.axiosPutWrapper(`/documents/update/${this.documentId}`, dataToSendToServer)
        }

        this.lmModalSuccessWithButtons({
          modalClosable: false,
          modalKeyboard: false,
          resultSubtitle: '',
          message: this.$t('document_started_success_msg', { documentName: this.documentForm?.documentName }),
          buttons: [
            {
              btnLink: '/inbox',
              btnText: this.$t('continue')
            },
            {
              btnText: this.$t('view_document'),
              btnCallback: async () => {
                if (this.isOnEditMode) {
                  await this.getDocument()
                  this.refreshVueComponents()
                } else {
                  this.$router.push(`/documentos/${this.documentTypeId}/editar/${response.data._id}`)
                }
              }
            }
          ]
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },

    async onBeforeStart () {
      let immediate = false
      try {
        const me = this.$store.getters['session/userInfo']
        const firstParticipant = this.documentForm.workflow.flat().find(el => el.action === 'S' || el.action === 'R')
        const hasEditors = this.documentForm.workflow.flat().some(el => el.action === 'E')
        if (
          this.lumbreVersion === 2 &&
          this.typeSignature === 'electronic_without_otp' &&
          !hasEditors &&
          firstParticipant.id === me.id
        ) {
          this.lmModalInformationMessage({
            // modalClosable: false,
            // modalKeyboard: false,
            resultSubtitle: '',
            buttons: [
              {
                btnText: this.$t('i_will_do_it_later'),
                btnType: 'danger',
                btnGhost: true,
                btnCallback: async () => {
                  immediate = false
                  await this.onStart(immediate)
                }
              },
              {
                btnText: this.$t('continue'),
                btnCallback: async () => {
                  immediate = true
                  await this.onStart(immediate)
                }
              }
            ],
            message: firstParticipant.action === 'R' ? this.$t('confirmations.immediate_review_document') : this.$t('confirmations.immediate_sign_document')
          })
        } else {
          await this.onStart(immediate)
        }
      } catch (error) {
        console.log(error)
      }
    },

    async onStart (immediate) {
      this.isProcessing = true
      try {
        this.validateParticipants()
        await this.validateForm(this.$v)
        await this.$refs.lmFormbuilder.validate()
        await this.participantsLicencesNotification()

        const targetEndpoint = !this.isOnEditMode ? '/documents/createandstart' : '/documents/start'
        const res = await commonsApi.axiosPostWrapper(targetEndpoint, {
          id: this.$route.params.id,
          ...this.documentForm,
          collaborators: this.collaboratorsCopy,
          documentTypeId: this.documentTypeId
        })

        if (immediate) {
          const documentId = res.data._id
          await this.getDocument(documentId)
          await this.onBeforeSign(documentId, immediate)
        }

        // Si este documento se crea como parte de un proceso (trámite)
        if (this.fromProcess) {
          this.setDocument({
            id: res.data._id,
            name: res.data.documentName
          })
          this.lmNotificationSuccessMessage({
            message: this.$t('good_title'),
            description: this.$t('start_document_confirmation', { documentName: this.documentForm?.documentName })
          })
          this.$router.push(this.processPathToGoBack)
        } else {
          if (immediate) return
          this.lmModalSuccessWithButtons({
            modalClosable: false,
            modalKeyboard: false,
            resultSubtitle: '',
            message: this.$t('document_started_success_msg', { documentName: this.documentForm?.documentName }),
            buttons: [
              {
                btnLink: '/inbox',
                btnText: this.$t('continue')
              },
              {
                btnText: this.$t('view_document'),
                btnCallback: async () => {
                  if (this.isOnEditMode) {
                    await this.getDocument()
                    this.refreshVueComponents()
                  } else {
                    this.$router.push(`/documentos/${this.documentTypeId}/editar/${res.data._id}`)
                  }
                }
              }
            ]
          })
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },

    async onReject () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPostWrapper(`/documents/reject`, {
          id: this.documentId
        })
        this.lmToastSuccessMessage(`Se rechazó el documento "${this.documentForm?.documentName || ' el documento'}" correctamente.`, 'Documento rechazado.')
        this.$router.push('/inbox')
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    async onRefresh () {
      this.isProcessing = false
      if (this.lumbreVersion === '2') {
        await this.getDocument()
        this.refreshVueComponents()
      } else {
        this.$refs.lmDocumentsV1.onRefresh()
      }
    },
    async onTake () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPostWrapper(`/documents/take`, {
          id: this.documentId
        })
        this.lmToastSuccessMessage(`Se tomó el documento "${this.documentForm?.documentName || ' el documento'}" correctamente.`, 'Documento tomado.')
        await this.getDocument()
        this.refreshVueComponents()
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },

    onInPersonSignConfirmation () {
      this.isProcessing = true
      this.lmModalInformationMessage({
        modalClosable: false,
        modalKeyboard: false,
        resultSubtitle: '',
        buttons: [
          {
            btnText: this.$t('cancel'),
            btnType: 'danger',
            btnGhost: true,
            btnCallback: () => {
              this.isProcessing = false
            }
          },
          {
            btnText: this.$t('sign'),
            btnCallback: () => {
              this.onBeforeSign()
            }
          }
        ],
        message: this.$t('confirmations.inperson_start_sign_confirmation', { name: this.nextParticipantName })
      })
    },

    async onBeforeSign (id = this.documentId, immediate = false) {
      this.isProcessing = true
      if (!this.disableInPersonGeoLocationValidations) {
        this.signatureGeoLocation = await this.getLocation()
      }

      try {
        const { signature, id: loggedUserId } = this.$store.getters['session/userInfo']

        const nextSignerUserType = this.documentForm.lumbreNextSigner?.userType || null

        let base64File = null
        let signatureFilePath = null
        let signatureMeta = null
        let signaturePersistence = true
        let signerId = this.documentForm.lumbreNextSigner.id

        // Firmante presencial
        if (nextSignerUserType === 'inperson') {
          // Geolocalización
          if (!this.disableInPersonGeoLocationValidations) {
            if (this.inPersonSignatureValidations.includes('geolocation')) {
              if (!this.signatureGeoLocation) {
                this.lmModalInformationMessage({
                  resultSubtitle: '',
                  message: 'La geolocalización es requerida para firmar este documento.'
                })
                throw new Error('La geolocalizacion es obligatoria.')
              }
            }
          }
          // lmSignaturePad
          if (this.inPersonSignatureValidations.includes('holographic')) {
            base64File = await this.$refs.lmSignaturePad.open()
          }

          // WacomSignaturePad
          if (this.inPersonSignatureValidations.includes('holographic_wacom')) {
            const data = await this.$refs.WacomSignaturePad.open(this.nextParticipantName, this.nextParticipantInCharacterOf)
            base64File = data.img
            signatureMeta = data
          }
        }

        // Firmante interno
        if (nextSignerUserType === 'internal') {
          // Si el proximo firmante es interno, siempre deberia ser el usuario logueado
          // puede ser un reemplazante
          signerId = loggedUserId

          // Geolocalización
          if (!this.disableInPersonGeoLocationValidations) {
            if (this.internalSignatureValidations.includes('geolocation')) {
              if (!this.signatureGeoLocation) {
                this.lmModalInformationMessage({
                  resultSubtitle: '',
                  message: 'La geolocalización es requerida para firmar este documento.'
                })
                throw new Error('La geolocalizacion es obligatoria.')
              }
            }
          }

          // Firma del perfil del usuario
          if (this.internalSignatureValidations.includes('holographic_simple')) {
            if (!signature) {
              base64File = await this.$refs.lmSignaturePad.open()
            } else {
              signatureFilePath = signature
            }
          }

          // Firmar en este momento
          if (this.internalSignatureValidations.includes('holographic_advanced')) {
            if (this.internalSignatureValidationsCapture === 'holographic_simple_capture') {
              // lmSignaturePad
              base64File = await this.$refs.lmSignaturePad.open()
            } else if (this.internalSignatureValidationsCapture === 'holographic_wacom_capture') {
              // WacomSignaturePad
              const data = await this.$refs.WacomSignaturePad.open(this.nextParticipantName, this.nextParticipantInCharacterOf)
              base64File = data.img
              signatureMeta = data
            }
            signaturePersistence = false
          }
        }

        if (base64File) {
          const { data: filePath } = await commonsApi.axiosPutWrapper('/users/upload-signature/' + signerId, {
            signature: base64File,
            persistence: signaturePersistence
          })

          signatureFilePath = filePath

          // Si puedo firmar, la firma es persistente y es un firmante interno,
          // guardo su firma en vuex
          if (
            this.actionsAvailables.canBeSignedByMe &&
            signaturePersistence &&
            nextSignerUserType === 'internal'
          ) {
            this.$store.commit('session/mutateSignature', filePath)
          }
        }

        if (signatureMeta) {
          await commonsApi.axiosPutWrapper('/documents/upload-signature-meta/' + this.documentId, {
            signature: signatureMeta,
            signer: this.documentForm.lumbreNextSigner
          })
          this.signatureMetaFilePath = signatureMeta
        }
        if (signatureFilePath) {
          this.signatureFilePath = signatureFilePath
        }

        if (nextSignerUserType === 'inperson') {
          return await this.onSign(id)
        }

        if (immediate) {
          return this.onSign(id)
        }

        this.lmModalInformationMessage({
          modalClosable: false,
          modalKeyboard: false,
          resultSubtitle: '',
          buttons: [
            {
              btnText: this.$t('cancel'),
              btnType: 'danger',
              btnGhost: true,
              btnCallback: () => {
                this.isProcessing = false
              }
            },
            {
              btnText: this.$t('sign'),
              btnCallback: () => {
                this.onSign(id)
              }
            }
          ],
          message: this.$t('confirmations.start_sign_confirmation')
        })
      } catch (error) {
        console.log(error)
        this.isProcessing = false
      }
    },

    async onSign (id = this.documentId) {
      const documentId = id
      this.isProcessing = true
      const inPersonSign = this.documentForm?.lumbreNextSigner?.userType === 'inperson' && this.actionsAvailables.canDelegateSignature

      try {
        if (inPersonSign) {
          await commonsApi.axiosPostWrapper(`/documents/sign`, {
            id: documentId,
            location: this.signatureGeoLocation
          })

          this.lmModalSuccessWithButtons({
            modalClosable: false,
            modalKeyboard: false,
            resultSubtitle: '',
            message: this.$t('document_inperson_signed_success_msg', { documentName: this.documentForm?.documentName }),
            buttons: [
              {
                btnLink: '/inbox',
                btnText: this.$t('continue')
              },
              {
                btnText: this.$t('view_document'),
                btnCallback: async () => {
                  await this.getDocument()
                  this.refreshVueComponents()
                }
              }
            ]
          })
          return
        } else {
          // Electronica
          if (this.typeSignature !== 'digital') {
            if (this.typeSignature === 'electronic') {
              await commonsApi.axiosPostWrapper('/documents/generateotp', {
                id: documentId
              })
              this.$refs.lmValidationModalFirmarToken.setIsCommentProps(true, true, 'Codigo', 'token')
              await this.$refs.lmValidationModalFirmarToken.lmShow()
            }

            await commonsApi.axiosPostWrapper(`/documents/sign`, {
              id: documentId,
              token: this.otpToken,
              signature: this.signatureFilePath,
              location: this.signatureGeoLocation
            })

            if (!this.fromProcess) {
              this.lmModalSuccessWithButtons({
                modalClosable: false,
                modalKeyboard: false,
                resultSubtitle: '',
                message: this.$t('document_signed_success_msg', { documentName: this.documentForm?.documentName }),
                buttons: [
                  {
                    btnLink: '/inbox',
                    btnText: this.$t('continue')
                  },
                  {
                    btnText: this.$t('view_document'),
                    btnCallback: async () => {
                      if (this.isOnEditMode) {
                        await this.getDocument()
                        this.refreshVueComponents()
                      } else {
                        this.$router.push(`/documentos/${this.documentTypeId}/editar/${id}`)
                      }
                    }
                  }
                ]
              })
            }
          }
          // Digital
          if (this.typeSignature === 'digital') {
            const nextSignerUserType = this.documentForm.lumbreNextSigner?.userType || null
            if (
              nextSignerUserType === 'internal' &&
              this.internalSignatureValidations.includes('holographic_advanced')
            ) {
              await commonsApi.axiosPostWrapper('/documenthistory/append-movement', {
                id: documentId,
                participant: this.documentForm.lumbreNextSigner,
                data: {
                  signatureFilePath: this.signatureFilePath,
                  signatureMeta: this.signatureMetaFilePath,
                  location: this.signatureGeoLocation
                }
              })
            }

            if (this.enableLumbredocsSigner) {
              await this.checkLumbreDocsSigner()
              const response = await commonsApi.axiosGetWrapper(`/documents/generatejnlpV2/${documentId}`)
              await commonsApi.axiosPostWrapper(`http://localhost:${this.lumbreSignerPort}/initSR`, {
                url: response.data.targetURL
              })
            } else {
              const tempLink = document.createElement('a')
              tempLink.href = `${process.env?.VUE_APP_API_URL}documents/generatejnlp/${documentId}`
              tempLink.setAttribute('download', `documento_afirmar.jnlp`)
              tempLink.click()
              this.lmToastSuccessMessage(`Para continuar debe finalizar el proceso de firma desde la aplicacion.`, 'Enviado al Firmador.')
            }
          }
        }

        if (this.typeSignature !== 'digital') {
          this.lmNotificationSuccessMessage({
            message: this.$t('good_title'),
            description: this.$t('document_signed_success_msg', { documentName: this.documentForm?.documentName })
          })
        }

        // Si estoy creando desde un trámite
        if (this.fromProcess) {
          this.$router.push(this.processPathToGoBack)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
        this.signatureFilePath = null
        this.signatureMetaFilePath = null
        this.signatureGeoLocation = false
      }
    },

    getLocation () {
      return new Promise((resolve, reject) => {
        const options = {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }

        navigator.geolocation.getCurrentPosition(
          (position) => resolve({
            timestamp: position.timestamp,
            accuracy: position.coords.accuracy,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }),
          // (error) => reject(error),
          (error) => {
            console.log(error)
            resolve()
          },
          options
        )
      })
    },

    onReviewConfirmation () {
      this.isProcessing = true
      this.lmModalInformationMessage({
        modalClosable: false,
        modalKeyboard: false,
        resultSubtitle: '',
        buttons: [
          {
            btnText: this.$t('cancel'),
            btnType: 'danger',
            btnGhost: true,
            btnCallback: () => {
              this.isProcessing = false
            }
          },
          {
            btnText: this.$t('continue'),
            btnCallback: () => {
              this.onReview()
            }
          }
        ],
        message: this.$t('confirmations.review_confirmation')
      })
    },

    async onReview () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPostWrapper(`/documents/review`, {
          id: this.documentId
        })

        this.lmModalSuccessWithButtons({
          modalClosable: false,
          modalKeyboard: false,
          resultSubtitle: '',
          message: this.$t('document_approved_success_msg', { documentName: this.documentForm?.documentName }),
          buttons: [
            {
              btnLink: '/inbox',
              btnText: this.$t('continue')
            },
            {
              btnText: this.$t('view_document'),
              btnCallback: async () => {
                await this.getDocument()
                this.refreshVueComponents()
              }
            }
          ]
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },

    async onDelete () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPutWrapper(`/documents/delete/${this.documentId}`)
        this.lmToastSuccessMessage(`Se eliminó ${this.documentForm?.documentName || ' el documento'} correctamente.`, 'Documento eliminado.')
        this.$router.push('/inbox')
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },

    async participantsLicencesNotification () {
      const participants = this.documentForm.participants
        .filter(el => !el?.userType || (el?.userType !== 'external' && el?.userType !== 'inperson'))
        .map(el => el.id)
      const response = await commonsApi.axiosPostWrapper('/documents/validate-participants', {
        participants
      })
      if (response.data.length) {
        let content = ''
        response.data.forEach(el => {
          let substitute = ''
          if (el.substitute) {
            substitute = `Esta siendo reemplazado por ${el.substitute.name}`
          }
          const user = this.documentForm.participants.find(participant => participant.id === el.id)
          content += `<p>${user.name} tiene licencia hasta el ${el.end}. ${substitute}</p>`
        })
        this.$refs.lmConfirmationModalLicenses.setMessageProps(content)
        await this.$refs.lmConfirmationModalLicenses.lmShow()
      }
    },

    async onResendEmailToExternal () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPostWrapper('/documents/resendEmail-to-external', {
          id: this.documentId
        })
        this.lmToastSuccessMessage('Se reenvió la notificación correctamente', 'Enviado con éxito')
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },

    checkForInvalidParticipantsConditions () {
      if (this.isOnePersonFormat) return false
      return ((
        this.lumbreVersion === 1 &&
            (this.documentForm?.participants?.at(-1)?.action === 'R' ||
            !this.documentForm?.participants?.some(el => el.action === 'S'))
      ) ||
        (
          this.lumbreVersion === 2 &&
            (!this.documentForm.workflow.flat().some(el => el.action === 'S') ||
            this.documentForm.workflow.flat().filter(el => el.action === 'R' || el.action === 'S').at(-1).action === 'R')
        ))
    },

    validateParticipants () {
      if (this.checkForInvalidParticipantsConditions()) {
        this.lmToastDangerMessage('Lo sentimos pero el último participante debe ser un firmante.', 'Error')
        throw new Error('El último participante debe ser un firmante.')
      }
    },

    onToolbarModeChange (event) {
      this.toolbarMode = event
    },

    async onUpdateExpirationDate (event) {
      this.documentForm.expirationDate = event
    },

    onContentTemplateChange (event) {
      for (const key in this.documentForm) {
        if (event.hasOwnProperty(key)) {
          this.documentForm[key] = event[key]
        }
      }
      this.lmDocumentWorkflowKey++
    },

    updateDocumentForm (event) {
      this.documentForm = Object.assign({}, this.documentForm, event)
      if (!this.$v.$invalid && !this.$refs.lmFormbuilder.$v.$invalid) {
        this.step0completed = true
      } else {
        this.step0completed = false
      }
    },

    onHideCollaborators () {
      if (this.lumbreVersion === 2) {
        this.hideWorkflowCollaborators = true
      }
    },
    onEditorChange (event) {
      let index = -1
      if (event.subelement) {
        index = this.collaboratorsCopy.findIndex(el => el.subelement === event.subelement)
      } else {
        index = this.collaboratorsCopy.findIndex(el => el.element === event.element.formObjectToSendToServerProperty)
      }
      const item = {
        ...event.editor,
        action: 'E',
        element: event.element.formObjectToSendToServerProperty,
        subelement: event.subelement || null
      }
      if (index > -1) {
        this.collaboratorsCopy.splice(index, 1, item)
      } else {
        this.collaboratorsCopy.push(item)
      }
    },

    // Refresco los components de vue en caso de permanecer en la misma view
    // Nota. esto no deberia ser necesario pero lo es ya que algunos componentes
    // no tienen la capacidad de actualizarse mediante una escucha activa de los datos
    refreshVueComponents () {
      this.isLoading = true
      this.$nextTick(() => { this.isLoading = false })
    },

    stepStatus (step) {
      if (this.stepsMovements.includes(step) && !this[`step${step}completed`]) {
        return 'error'
      } else if (this.stepsMovements.includes(step) && this[`step${step}completed`]) {
        return 'finish'
      }
      return 'wait'
    },

    setToken (data) {
      this.otpToken = data.comment
    },

    validateForm (validations) {
      return new Promise((resolve, reject) => {
        let error = false
        validations.$touch()
        error = validations.$error
        if (error) {
          for (const key in this.documentForm) {
            if (validations.documentForm.hasOwnProperty(key)) {
              if (validations.documentForm[key].$error) {
                // const selector = this.$el.querySelector(`#${key}`)
                // selector.scrollIntoView({ behavior: 'smooth' })
                break
              }
            }
          }
          this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error de validación')
          reject(new Error('validation error'))
        } else {
          resolve(true)
        }
      })
    },

    async getAttachments () {
      const arr = []
      const response = await commonsApi.axiosGetWrapper(this.formbuilderUri)
      const uploadersFormElement = response.data.formElements.filter(el => el.componentName === 'LmUploadFiles')
      if (uploadersFormElement.length) {
        for (const element of uploadersFormElement) {
          if (this.documentForm[element.formObjectToSendToServerProperty]) {
            arr.push(this.documentForm[element.formObjectToSendToServerProperty])
          }
        }
      }
      if (arr.length) {
        this.attachmentDocuments = arr.flat().map(el => {
          return {
            ...el,
            path: process.env.VUE_APP_API_URL_STATIC_ASSETS + el.path
          }
        })
      }
    },

    async getDocumentType () {
      const response = await commonsApi.axiosGetWrapper('/documenttypes/' + this.documentTypeId)
      this.documentTypeForm = response.data
    },
    async getDocument (id = this.documentId) {
      if (!id) return

      const response = await commonsApi.axiosGetWrapper('/documents/' + id)
      this.documentForm = response.data

      if (!this.documentForm?.documentExpirationDate) {
        this.documentForm.expirationDate = undefined
      }

      this.lumbreVersion = response.data.lumbreVersion || Number(process.env?.VUE_APP_DOCUMENTS_VERSION) || 1

      if (this.lumbreVersion === 2) {
        this.actionsAvailables = response.data?.actions_availables || {}
      } else {
        for (const key in this.actionsAvailables) {
          if (response.data.hasOwnProperty(key)) {
            // Nota. Esto es horrible pero la idea es no tocar el backend para documentos v1
            this.actionsAvailables[key] = typeof response.data[key] === 'boolean' ? response.data[key] : response.data[key].value || false
          }
        }
      }

      if (this.actionsAvailables.canReSendEmailToExternalSigner) {
        const link = await commonsApi.axiosGetWrapper('/documenthistory/signature-external-link/' + id)
        this.externalParticipantLink = link.data
      }
    },

    async getLicence () {
      if (process.env?.VUE_APP_LUMBRE_LICENSES_ENABLED === 'yes') {
        const response = await commonsApi.axiosGetWrapper('/licenses/imonlicense')
        this.isOnLicense = response.data
      } else {
        this.isOnLicense = false
      }
    },

    async getAssociations () {
      if (!this.isOnEditMode) return
      const response = await commonsApi.axiosGetWrapper(`/documents/${this.documentId}/associated-processes`)
      if (response.data.length) {
        this.hasAssociatedProcesses = true
        this.associatedProcesses = response.data
      }
    },

    async documentTypeLastPublishedVersion () {
      if (!this.isOnEditMode && !this.documentTypeForm.publish) {
        const response = await commonsApi.axiosGetWrapper('/documenttypes/last-version/' + this.documentTypeId)
        if (response.data !== this.documentTypeId) {
          this.$router.replace(`/documentos/${response.data}/nuevo`)
        } else {
          this.lmModalDangerMessage({
            btnText: this.$t('go_back'),
            btnLink: 'goback',
            modalClosable: false,
            modalKeyboard: false,
            resultSubtitle: this.$t('talk_to_admin'),
            message: this.$t('document_type_not_published')
          })
        }
      }
    },
    unsubscribeSockets () {
      console.log('unsubscribeSockets')
      if (process.env.VUE_APP_SOCKET_ENABLED === 'yes') {
        this.sockets.unsubscribe(`onDocument_${this.documentId}`)
      }
    }
  },

  watch: {
    async currentStep (val, oldVal) {
      this.stepsMovements.push(oldVal)
    },
    'documentForm.workflow': {
      deep: true,
      handler (val) {
        if (this.isOnePersonFormat || val.flat().some(el => el.action === 'S')) {
          this.step1completed = true
        } else {
          this.step1completed = false
        }
        // Por retro compatibilidad
        if (this.lumbreVersion === 2 && !this.isOnePersonFormat) {
          this.documentForm.participants = val.flat().filter(el => el.action === 'S' || el.action === 'R')
        }
      }
    },
    'documentForm.participants': {
      deep: true,
      handler (val) {
        if (this.lumbreVersion === 1 && val) {
          if (val.length && val.some(el => el.action === 'S')) {
            this.step1completed = true
          } else {
            this.step1completed = false
          }
        }
      }
    },
    'documentForm.documentName' (val) {
      if (!this.$v.$invalid && this.$refs.hasOwnProperty('lmFormbuilder') && !this.$refs.lmFormbuilder.$v.$invalid) {
        this.step0completed = true
      } else {
        this.step0completed = false
      }
    }
  },

  async created () {
    if (process.env.VUE_APP_SOCKET_ENABLED === 'yes') {
      console.log('subscribeSockets')
      const key = `open${Date.now()}`
      this.sockets.subscribe(`onDocument_${this.documentId}`, async _ => {
        this.isProcessing = true
        notification.success({
          message: 'Documento actualizado',
          description:
          'Por favor, tenes que refrescar para ver las novedades.',
          duration: 0,
          btn: h => {
            return h(
              'a-button',
              {
                props: {
                  type: 'primary',
                  size: 'small'
                },
                on: {
                  click: () => {
                    this.onRefresh()
                    notification.close(key)
                  }
                }
              },
              this.$t('refresh')
            )
          },
          key,
          onClose: this.onRefresh
        })
      })
    }
    // Evito recargar contenidos de version 2 si estamos en version 1, esto evita los dobles request al
    // backend que se estaban generando.
    if (this.lumbreVersion === 1) {
      return
    }
    try {
      this.isLoading = true
      await this.getDocumentType()
      await this.documentTypeLastPublishedVersion()
      await this.getDocument()
      await this.getAttachments()
      await this.getLicence()
      await this.getAssociations()
      if (this.isOnEditMode) {
        this.stepsMovements = [0, 1, 2]
        this.step0completed = true
        this.step1completed = true
      }

      // NOTA. hago una copia para pasarlo por "formValues"
      // ya que "documentForm" se pasa como "formValues"
      // que es un watch que modifica "formObjectToSendToServer"
      // y esto produce que se emita "@formObjectToSendToServerChange"
      // que vuelve a modificar "documentForm" produciendo un loop infinito
      // Es raro pero "formValues" no funciona si se pasa el mismo objecto
      // que va a actualizarse con el evento "@formObjectToSendToServerChange"
      this.documentFormCopy = _.cloneDeep(this.documentForm)
      if (this.isOnEditMode) {
        this.collaboratorsCopy = _.cloneDeep(this.documentForm.collaborators)
      }
      if (!this.documentWasStarted) {
        this.showAllFormFields = false
      }
    } catch (error) {
      if (error.response?.data?.type === 'ACCESS_DENIED') {
        this.lmModalDangerMessage({
          btnText: this.$t('go_back'),
          btnLink: 'goback',
          modalClosable: false,
          modalKeyboard: false,
          resultSubtitle: this.$t('talk_to_admin'),
          message: this.$t('document_access_denied')
        })
        this.accessDeniedFeedback = true
      }
      console.log(error)
    } finally {
      this.isLoading = false
    }
  },

  async mounted () {
    // Firma desde el enlace de una notificacion push
    if (
      this.isOnEditMode &&
      this.$route.query.immediateSign === 'true' &&
      this.actionsAvailables.canBeSignedByMe
    ) {
      await this.onBeforeSign()
    }
  },

  beforeRouteLeave (to, from, next) {
    // Por https://app.clickup.com/t/85zrtw5ng al salir, si corresponde, vacio el
    // registro del trámite desde el que se viene, para que el boton diga "Cancelar"
    // en vez de "Volver al Trámite"
    if (to?.name !== 'process-editar' && this.fromProcess) {
      this.clearProcessesStore()
    }
    this.unsubscribeSockets()
    next()
  }
})
</script>

<style scoped>
.page-header {
  position: sticky;
  top: 0;
  background-color: white;
  padding: .75rem 1.25rem;
  z-index: 4;
  border-bottom: 2px solid #eaeaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 95px;
}

@media all and (max-width: 768px) {
  .page-header {
    padding: .4rem .8rem;
    height: auto;
  }
  .page-header h2 {
    font-size: 1.2rem;
    margin-bottom: 5px !important;
  }
}

.page-content {
  width: calc(100% + 1rem);
  min-height: calc(100vh - 70px - 60px - 95px);
  margin-left: auto;
  /* display: flex; */

  display: grid;
  grid-template-columns: calc(100% - 100px) 100px;
}

.access-denied-feedback-content {
  min-height: calc(100vh - 70px - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-squad-icon {
  width: 10px;
  height: 10px;
  display: inline-block;
  vertical-align: inherit;
  margin: 0 6px 0 0;
  background-color: orange;
}

.status-button {
  background-color: orange;
  color: white;
}

.status-button:hover {
  background-color: orange;
  color: white;
  box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
  transform: scale(1.05);
  transition: ease-in-out .25s transform;
  border-color: rgba(149, 157, 165, 0.2);
}

.status-button:focus {
  border-color: rgba(149, 157, 165, 0.2);
}

.status-squad-icon.color-2, .status-button.color-2, .status-button:hover.color-2 {
  background-color: #007bff;
}

.status-squad-icon.color-3, .status-button.color-3, .status-button:hover.color-3 {
  background-color: #97077D;
}

.status-squad-icon.color-4, .status-button.color-4, .status-button:hover.color-4 {
  background-color: #6FB200;
}

.status-squad-icon.color-6, .status-button.color-6, .status-button:hover.color-6 {
  background-color: #00872A;
}

.status-squad-icon.color-7, .status-button.color-7, .status-button:hover.color-7 {
  background-color: rebeccapurple;
}

.status-squad-icon.color-9, .status-button.color-9, .status-button:hover.color-9 {
  background-color: red;
}
</style>
